export const SHOPIFY_QUEUE_FILTERS = [
  ['all', 'All'], ['ready', 'Ready to draft'], ['drafts', 'Drafts to price'],
  ['ready_to_publish', 'Ready to publish'], ['live', 'Live'], ['needs_attention', 'Needs attention'],
];

export function queuePrice(value) {
  const text = String(value ?? '').trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(text)) return null;
  const [whole, fraction = ''] = text.split('.');
  const integer = whole.replace(/^0+(?=\d)/, '');
  if (integer.length > 8) return null;
  const normalized = `${integer}.${fraction.padEnd(2, '0')}`;
  return /^0\.00$/.test(normalized) ? null : normalized;
}

export function queueGbp(value) {
  const normalized = queuePrice(value);
  if (!normalized) return 'Not set';
  const [whole, fraction] = normalized.split('.');
  return `£${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${fraction}`;
}

export function queueRowVersion(row) {
  return JSON.stringify([row?.jobId ?? null, row?.jobVersion ?? null, row?.updatedAt ?? null, queuePrice(row?.approvedPriceGbp), queuePrice(row?.savedPriceGbp)]);
}

export function queuePriceEntry(row) {
  return { value: queuePrice(row.savedPriceGbp) || queuePrice(row.approvedPriceGbp) || '', version: queueRowVersion(row) };
}

export function queuePriceState(row, entry) {
  const current = entry || queuePriceEntry(row);
  const price = queuePrice(current.value);
  return {
    price,
    dirty: (String(current.value).trim() !== '' && !price) || price !== (queuePrice(row.savedPriceGbp) || queuePrice(row.approvedPriceGbp)),
    needsApproval: !!price && price !== queuePrice(row.approvedPriceGbp),
    stale: current.version !== queueRowVersion(row),
  };
}

export function filterQueueRows(rows, filter = 'all', query = '') {
  const terms = String(query).trim().toLowerCase().split(/\s+/).filter(Boolean);
  return rows.filter(row => {
    const statusMatches = filter === 'all' || (filter === 'needs_attention' && row.status !== 'live' && row.publishIntent?.status === 'needs_attention') || (filter === 'drafts' ? ['draft', 'drafting'].includes(row.status) : row.status === filter);
    const text = [row.certNumber, row.name, row.setName, row.number, row.location, row.grade].filter(x => x != null).join(' ').toLowerCase();
    return statusMatches && terms.every(term => text.includes(term));
  });
}

export function queueCanAct(row, operation, entry, pending = false) {
  if (pending || !row.actions?.[operation]) return false;
  const price = queuePriceState(row, entry);
  if (operation === 'price') return !!row.jobId && !!price.price && price.dirty && !price.stale;
  if (operation === 'savePrice') return !!price.price && !price.stale && (price.dirty || price.needsApproval && !!row.actions.price);
  if (operation === 'draftAndPublish') return !!price.price && !price.stale;
  if (operation === 'publish') return !!row.jobId && !!queuePrice(row.approvedPriceGbp) && !price.dirty && !price.needsApproval && !price.stale;
  return true;
}

export function queueCommand(row, operation, requestId, entry) {
  if (!requestId) throw Error('A request ID is required.');
  const command = { operation, slabId: row.slabId, requestId };
  if (['draft', 'savePrice', 'draftAndPublish'].includes(operation)) command.expectedUpdatedAt = row.updatedAt;
  else {
    command.jobId = row.jobId;
    command.expectedVersion = row.jobVersion;
    if (operation === 'price') {
      command.priceGbp = queuePrice(entry?.value);
      if (!command.priceGbp) throw Error('Enter a positive GBP price with no more than two decimal places.');
    }
  }
  if (operation === 'savePrice' || operation === 'draftAndPublish') {
    command.priceGbp = queuePrice(entry?.value);
    if (!command.priceGbp) throw Error('Enter a positive GBP price with no more than two decimal places.');
  }
  return command;
}

export function queuePendingKey(email) {
  return `rand-shopify-queue-pending:${String(email || '').trim().toLowerCase()}`;
}

export function readQueuePending(email, storage) {
  try {
    const raw = (storage || globalThis.sessionStorage).getItem(queuePendingKey(email));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.some(item => !item?.command?.slabId || !item.command.requestId || !['draft', 'draftAndPublish', 'price', 'savePrice', 'publish', 'retry'].includes(item.command.operation))) {
      throw Error('Invalid previous action record.');
    }
    return parsed;
  } catch {
    throw Error('Previous Shopify actions could not be read. Keep this tab open and restore browser session storage before making changes.');
  }
}

export function writeQueuePending(email, pending, storage) {
  try {
    const target = storage || globalThis.sessionStorage;
    if (pending.length) target.setItem(queuePendingKey(email), JSON.stringify(pending));
    else target.removeItem(queuePendingKey(email));
    return true;
  } catch { return false; }
}

// A confirmed existing draft fulfils a draft intent even when its original
// browser acknowledgement was lost. This is not a receipt for the request ID.
// Combined requests can also be acknowledged by their exact immutable server intent.
// Other price changes, publication and job retries retain their exact receipts.
export function reconcileQueuePending(pending, rows) {
  const resolved = [], remaining = [];
  for (const item of pending) {
    const command = item?.command;
    const matches = rows.filter(row => row.slabId === command?.slabId);
    const row = matches.length === 1 ? matches[0] : null;
    if (command?.operation === 'draft' && typeof item.certNumber === 'string' && item.certNumber
      && row?.certNumber === item.certNumber && row.draftConfirmed === true
      && typeof row.jobId === 'string' && row.jobId
      && (!command.jobId || command.jobId === row.jobId)
      && ['draft', 'ready_to_publish', 'live'].includes(row.status)) resolved.push(item);
    else if (command?.operation === 'draftAndPublish' && item.certNumber && row?.certNumber === item.certNumber
      && typeof row.jobId === 'string' && row.jobId && row.publishIntent?.jobId === row.jobId
      && row.publishIntent?.slabId === command.slabId && row.publishIntent?.requestId === command.requestId
      && ['pending', 'queued', 'needs_attention'].includes(row.publishIntent?.status)
      && queuePrice(command.priceGbp) && queuePrice(row.publishIntent?.priceGbp) === queuePrice(command.priceGbp)) resolved.push(item);
    else remaining.push(item);
  }
  return { pending: remaining, resolved };
}

// Each result is retained independently; one rejection does not hide successful siblings.
export async function runQueueBatch(items, worker, onResult = () => {}) {
  const results = new Array(items.length);
  let next = 0;
  async function run() {
    while (next < items.length) {
      const index = next++;
      try { results[index] = { status: 'fulfilled', value: await worker(items[index]) }; }
      catch (reason) { results[index] = { status: 'rejected', reason }; }
      onResult(results[index], items[index]);
    }
  }
  await Promise.all(Array.from({ length: Math.min(2, items.length) }, run));
  return results;
}

export function queueReceipt(response, command) {
  if (!response || response.requestId !== command.requestId || response.slabId !== command.slabId || !['ready', 'drafting', 'draft', 'ready_to_publish', 'publishing', 'live', 'needs_attention'].includes(response.status)) {
    throw Error('The server response could not confirm this request. Check / retry the previous action.');
  }
  return response;
}

export function queueReceiptMessage(response, operation) {
  if (response.message) return response.message;
  if (response.status === 'live') return 'The action was acknowledged. Refresh to confirm the current live listing.';
  if (response.status === 'draft') return 'Draft confirmed.';
  if (operation === 'price' && response.status === 'ready_to_publish') return 'Exact selling price saved.';
  if (['drafting', 'publishing'].includes(response.status)) return 'Request accepted. Completion is not yet confirmed; refresh to check.';
  return `Server status: ${response.status}. Refresh to check the current state.`;
}

export const QUEUE_PRICING_STATES = ['not_checked', 'queued', 'checking', 'checked', 'failed', 'unavailable'];
export function queueGrade(value) {
  const stripped = String(value ?? '').trim().replace(/^(?:(?:ACE|PSA|CGC|BGS)\s*)+/i, '').trim();
  return /^\d+(?:\.\d+)?$/.test(stripped) ? String(Number(stripped)) : stripped;
}

export function queuePricing(row) {
  const value = row?.pricing;
  return {
    status: QUEUE_PRICING_STATES.includes(value?.status) ? value.status : 'not_checked',
    checkedAt: typeof value?.checkedAt === 'string' ? value.checkedAt : null,
    message: typeof value?.message === 'string' ? value.message : '',
    lanes: value?.lanes || {},
  };
}

export function queuePricingLabel(row) {
  const labels = { not_checked: 'Not checked yet', queued: 'Price check queued', checking: 'Checking prices', checked: 'Price check completed', failed: 'Price check failed', unavailable: 'Pricing unavailable' };
  return labels[queuePricing(row).status];
}

export function queueReferenceStatus(row, lane) {
  const pricing = queuePricing(row), detail = pricing.lanes[lane] || {};
  const status = detail.status || pricing.status;
  const completed = pricing.status === 'checked' && !!pricing.checkedAt && Number.isFinite(Date.parse(pricing.checkedAt));
  if (pricing.status === 'queued') return 'Check queued';
  if (pricing.status === 'checking') return 'Checking sources…';
  if (completed && ['no_match', 'no_sale', 'no_sales', 'no_results', 'not_found', 'empty'].includes(status)) return 'No sale found in sources checked';
  if (['queued', 'pending'].includes(status)) return 'Check queued';
  if (['checking', 'running'].includes(status)) return 'Checking sources…';
  if (['failed', 'error'].includes(status)) return 'Check failed';
  if (status === 'unavailable') return 'Source unavailable';
  if (status === 'range') return 'Sale found; exact amount unavailable';
  if (pricing.status === 'checked') return 'No sale result returned';
  return 'Not checked yet';
}

export function queueNeedsPriceCheck(row) {
  if (row?.pricing?.eligible === false) return false;
  return !queuePrice(row?.references?.ace?.price) || !queuePrice(row?.references?.psa?.price);
}

export function queueAutoCheckCerts(rows, attempted = new Set()) {
  return [...new Set(rows.filter(row => row?.pricing?.eligible !== false && !attempted.has(row.certNumber)
    && (row.pricing ? queuePricing(row).status === 'not_checked' : queueNeedsPriceCheck(row)))
    .map(row => row.certNumber).filter(cert => typeof cert === 'string' && cert.length > 0))].sort();
}

export function queuePricingReceipt(result, operation) {
  if (!result || ![operation, ...(operation === 'ensurePrices' ? ['checkPrices'] : [])].includes(result.operation)
    || !Number.isSafeInteger(result.requested) || result.requested < 0 || !result.pricing
    || typeof result.pricing.status !== 'string' || typeof result.pricing.message !== 'string'
    || !Array.isArray(result.pricing.checks) || result.pricing.checks.some(check => typeof check?.cert !== 'string' || typeof check?.status !== 'string')) {
    throw Error('The price-check request could not be confirmed. Refresh the queue to check its status.');
  }
  return result;
}
